import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Square, Clock, Hand } from 'lucide-react';
import { useTimerStore, elapsedSeconds, fmtClock, fmtMinutes } from '../lib/timerStore';
import { timeTrackingAPI } from '../lib/api';
import { MyTimeDialog } from './MyTimeDialog';

const useTick = (on) => {
  const [, setT] = useState(0);
  useEffect(() => { if (!on) return undefined; const id = setInterval(() => setT((t) => t + 1), 1000); return () => clearInterval(id); }, [on]);
};

export const FloatingTimer = () => {
  const navigate = useNavigate();
  const { active, todayMinutes, offsetMs, loaded, busy, refresh, stop, keepalive, myTimeOpen, setMyTimeOpen } = useTimerStore();
  const [method, setMethod] = useState('task');
  useTick(!!active);
  useEffect(() => { timeTrackingAPI.getConfig().then((c) => setMethod(c.method || 'task')).catch(() => {}); }, []);
  useEffect(() => {
    refresh();
    const id = setInterval(refresh, 30000);
    const onFocus = () => refresh();
    window.addEventListener('focus', onFocus);
    return () => { clearInterval(id); window.removeEventListener('focus', onFocus); };
  }, [refresh]);
  if (!loaded || (method === 'jornada' && !active)) return null;
  const sec = elapsedSeconds(active, offsetMs);
  const today = todayMinutes + Math.floor(sec / 60);
  return (
    <>
      <div className="fixed bottom-4 right-4 z-50 flex flex-col items-end gap-2" data-testid="floating-timer">
        {active?.warned_at && (
          <div className="rounded-xl border border-amber-300 bg-amber-50 px-3 py-2 text-sm shadow-lg flex items-center gap-2" data-testid="timer-warning">
            <span>¿Sigues con esta tarea? Si no, se parará sola.</span>
            <button onClick={keepalive} className="rounded-full bg-amber-500 px-3 py-1 text-white font-medium hover:bg-amber-600 inline-flex items-center gap-1" data-testid="timer-keepalive-btn"><Hand className="w-3.5 h-3.5" />Sigo</button>
          </div>
        )}
        {active ? (
          <div className="flex items-center gap-3 rounded-full border bg-background/95 backdrop-blur pl-4 pr-1.5 py-1.5 shadow-xl max-w-[calc(100vw-2rem)]" data-testid="timer-running">
            <span className="w-2.5 h-2.5 rounded-full bg-red-500 animate-pulse shrink-0" />
            <button className="text-left min-w-0 max-w-[120px] sm:max-w-[260px]" onClick={() => navigate(`/tasks?task=${active.task_id}`)} title="Abrir la tarea" data-testid="timer-task-link">
              <p className="text-sm font-medium truncate" data-testid="timer-task-title">{active.task_title}</p>
              <p className="text-xs text-muted-foreground truncate" data-testid="timer-client">{active.client_name || 'Cliente'}</p>
            </button>
            <span className="font-mono text-base font-semibold tabular-nums" data-testid="timer-elapsed">{fmtClock(sec)}</span>
            <button onClick={() => setMyTimeOpen(true)} className="hidden sm:inline text-xs text-muted-foreground hover:text-foreground whitespace-nowrap" data-testid="timer-today-total">Hoy: {fmtMinutes(today)}</button>
            <button onClick={stop} disabled={busy} className="rounded-full bg-red-500 hover:bg-red-600 text-white h-9 px-3 inline-flex items-center gap-1.5 text-sm font-medium transition-colors disabled:opacity-60" data-testid="timer-stop-btn">
              <Square className="w-3.5 h-3.5 fill-current" />Parar
            </button>
          </div>
        ) : (
          <button onClick={() => setMyTimeOpen(true)} className="rounded-full border bg-background/95 backdrop-blur px-3 py-1.5 text-xs text-muted-foreground shadow-md hover:text-foreground inline-flex items-center gap-1.5 transition-colors" data-testid="timer-idle-today">
            <Clock className="w-3.5 h-3.5" />Hoy: {fmtMinutes(today)}
          </button>
        )}
      </div>
      {myTimeOpen && <MyTimeDialog onClose={() => setMyTimeOpen(false)} />}
    </>
  );
};
